import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';
import { laserClampChunk, laserImpactFragmentShader } from '../lighting/laser-array.js';

// MirrorArray - ONE laser on the floor, and a grid of 64 motorised mirrors
// that multiply it. Techno, trance, progressive house, festival.
//
// The whole show is in the orientation of the mirrors. There is exactly one
// light source; every other beam in the frame is that beam, reflected, and
// the reflection is computed EXACTLY on the CPU for every mirror on every
// frame:   r = d - 2 (d . n) n
// There is no per-mirror beam direction stored anywhere: a choreography is a
// function of (col, row, step) that returns the mirror's NORMAL, the normal
// is what the gimbal shows, and the reflected beam is derived from it. A
// beam cannot disagree with the mirror it leaves, because the mirror is the
// only thing that decides where it goes.
//
// The decisive detail is the UNFOLDING: at every phrase the source goes dark,
// relights on a single mirror, and the fan of incident beams grows across the
// grid in the order the source sweeps it, the count doubling at equal
// intervals until all 64 are lit, over two beats. One beam becoming 64 is the
// image this visualizer exists for.
//
// Rendering is the laser primitive lighting/laser-array.js extracted from the
// cathedral: a camera-facing quad per beam with a pixel-floored width, white
// core, coloured halo, all intensity in rgb, alpha 1. Incident beams and
// reflected beams live in ONE InstancedMesh (up to 128 instances, one draw
// call), fed each frame by a handful of per-instance attributes written on
// the CPU - 64 reflections per frame is nothing, and it is what lets the law
// be exact rather than approximated in a shader.

// Shared-rig contract (see the framing addendum). 'forbidden', the same call
// laser-cathedral.js and beamwall.js make: a wash on the deck would destroy
// the contrast a laser needs to read as light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'mirrorarray',
  family: 'stage',
  energyLevel: 0.84,
  bpmMin: 115,
  bpmMax: 180,
  movement: 0.80,
  danceability: 0.85,
  tonality: 0.50,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

// --- the grid --------------------------------------------------------------
// n x n mirrors, with the central 2 x 2 removed: the grid sits behind the
// cover and its middle would be under the artwork anyway (the VinylWall
// convention). The span shrinks a little with the count so a 4 x 4 does not
// read as four lonely plates at the corners of the frame.
// samples: the private MSAA target (see _buildTarget). The shared chain has
// no anti-aliasing at all, and the plates, forks and chassis are lit solids
// with hard silhouettes under a camera that never stops drifting: their
// edges were pixelated and crawled. 0 renders straight into the shared
// scene (the Pi's tier 0 keeps every millisecond).
const TIER_LAYOUT = {
  2: { n: 8, span: 7.0, dichroic: true, impacts: true, reflection: true, samples: 4 },
  1: { n: 6, span: 6.4, dichroic: false, impacts: true, reflection: false, samples: 2 },
  0: { n: 4, span: 5.2, dichroic: false, impacts: false, reflection: false, samples: 0 }
};
const DEFAULT_TIER = 2;
const GRID_Z = -10.0;
const GRID_CY = 4.3;          // centre of the grid, world y
const PLATE_HALF = 0.31;       // the square plate, half side
const GLASS_HALF = 0.26;       // the reflective face inside the black frame
const PLATE_DEPTH = 0.09;

// The source: a laser on the floor, IN the frame, bottom left. It is placed
// in SCREEN space - 8% of the width from the left edge, low in the frame -
// and unprojected onto the floor at init (_placeSource), so it lands at the
// same spot whatever the aspect. The first version put it at x = -7.6 in
// world units, which is outside a 16:9 frame at that depth: the beams came
// from nowhere. SOURCE_FALLBACK is used only if the ray misses the floor.
const SOURCE_NDC_X = -0.84;
const SOURCE_NDC_Y = -0.66;
// The housing: a matt black box, 0.6 along the beam by 0.35 by 0.35, sitting
// ON the floor, its top edges chamfered so a key light draws them. It is
// yawed at the grid's centre (never pitched: a box on the floor stays flat)
// and the beam leaves through an aperture in the middle of its front face.
// 0.6 x 0.35 x 0.35 was the brief; at this depth (about 12.7 units, the
// 42-degree lens) that box filled 5% of the frame's width against a 4% cap,
// so it is scaled by 0.76. The proportions are the brief's.
const HOUSING_L = 0.46;
const HOUSING_W = 0.27;
const HOUSING_H = 0.27;
const HOUSING_CHAMFER = 0.028;
const HOUSING_BODY = 0.04;     // the matt black of the metal
const APERTURE_R = 0.04;
const SOURCE_Y = HOUSING_H / 2 + 0.004;   // the housing's centre: on the floor
const SOURCE_FALLBACK = [-5.4, HOUSING_H / 2 + 0.004, -2.0];
const EMITTER_OFFSET = HOUSING_L / 2 + 0.01;   // the aperture, on the front face
// The contact shadow under the box, and the spot the beam leaves on the
// floor just in front of the aperture (it anchors the source in the room).
const SHADOW_MARGIN = 0.16;
const SHADOW_DEPTH = 0.85;
const FLOOR_SPOT_AHEAD = 0.55;
const FLOOR_SPOT_STRETCH = 2.4;
const FLOOR_SPOT_GAIN = 0.80;

// Camera: a long-ish lens, the grid filling most of the frame height and its
// centre on the cover (the hole in the grid is where the artwork sits).
// scene-manager restores the base camera before every composition, so none
// of this leaks (see reinitialiserCamera).
const CAMERA_FOV = 42;
const CAMERA_POS = [0.0, 1.7, 8.0];
const CAMERA_LOOK = [0.0, 4.0, GRID_Z];
const FOG_DENSITY = 0.05;      // the brief's own figure
const FLOOR_COLOR = 0x0a0c11;
const FLOOR_SIZE = 90;
const BACKDROP_COLOR = 0x04060a;
// Pitched against the fog: FogExp2 at 0.05 leaves exp(-(0.05*18)^2) = 44%
// of a surface at the grid's distance, so a chassis painted its real dark
// grey would vanish.
const CHASSIS_COLOR = 0x3c4450;

// --- the beam --------------------------------------------------------------
// Two kinds of beam, and the hierarchy between them IS the image: the light
// has to be seen multiplying. An INCIDENT beam is the thread from the source
// to its mirror and nothing more - one pixel, a quarter of the opacity, no
// white core, no halo. A REFLECTED beam is the show - a three-pixel white
// core inside a ten-pixel coloured halo at full gain. Same instanced draw,
// aKind picks the profile per instance. The first version drew both with the
// same width and gain, and 64 + 64 identical lines read as one tangle.
const INCIDENT_PX = 1.0;
const INCIDENT_OPACITY = 0.25;
const INCIDENT_HALF_NEAR = 0.004;    // world half-width, under the pixel floor
const INCIDENT_HALF_FAR = 0.006;
const REFLECTED_PX = 10.0;           // the halo's full width, pixels
const REFLECTED_CORE_PX = 3.0;
const REFLECTED_HALF_NEAR = 0.012;
const REFLECTED_HALF_FAR = 0.030;
const BEAM_HOT_MIX = 0.85;
const BEAM_HALO_W = 0.45;
const PEAK_LUMINANCE = 0.95;
// Where a reflected beam dies in the haze: exponential extinction, then a
// hard close so nothing ever reaches the edge of the frame however loud the
// track gets. A beam that meets the floor first is cut there instead and the
// impact marks the arrival.
const BEAM_REACH = 34.0;
const BEAM_EXT = 0.028;
const BEAM_FADE_FROM = 22.0;
// Sixty incident beams leave ONE point: at the source they overlap
// completely and would sum to a white-out even at a quarter opacity, so each
// fades in over its first eighth - the flare sits there anyway.
const INCIDENT_FADE_IN = 0.12;
// FOCUS piles all 60 reflections onto one point; the overlap is the image,
// but the sum has to stay inside the bloom's range.
const FOCUS_GAIN = 0.45;
// The source itself, at the aperture: a 6 px point far brighter than
// anything else, a 30 px halo in the source's colour, and a thin anamorphic
// streak 15% of the frame's width. All in pixels (uFlareHalf, written each
// frame from the buffer size) so it reads the same at any resolution.
const FLARE_POINT_SIGMA_PX = 1.8;
const FLARE_HALO_SIGMA_PX = 8.0;
const FLARE_HALO_GAIN = 0.35;
const FLARE_STREAK_FRAC = 0.15;      // of the frame width, full length
const FLARE_STREAK_SIGMA_PX = 1.1;
const FLARE_STREAK_GAIN = 0.30;
const FLARE_HALF_H_PX = 26;
const IMPACT_SIZE = 0.42;
const REFLECTION_STRETCH = 4.0;
const REFLECTION_GAIN = 0.35;

// The shared safe-zone ramp bottoms out at 0.32 (SAFE_MIN_ATTENUATION in
// shaders/safeZone.glsl.js). The brief asks 0.45 here - remapped, not edited
// at the source, so every other visualizer keeps its own floor.
const SHARED_SAFE_FLOOR = 0.32;
const SAFE_FLOOR = 0.45;

// --- choreography ----------------------------------------------------------
const PAT_PARALLEL = 0, PAT_FOCUS = 1, PAT_FAN = 2, PAT_WAVE = 3, PAT_SPIRAL = 4;
// Internal only: the drop's convergence on the viewer. Never in the cycle.
const PAT_DROP_FOCUS = 5;

const PATTERN_NAMES = { parallel: PAT_PARALLEL, focus: PAT_FOCUS, fan: PAT_FAN, wave: PAT_WAVE, spiral: PAT_SPIRAL };

// A mirror on a motor does not glide: it snaps to its next position and
// holds. The orientation advances on an integer step counter that ticks once
// per beat; the normals ease from step-1 to step over MOVE_MS and then hold
// flat for the rest of the beat.
const MOVE_MS = 150;
// A pattern change crossfades over two beats so the grid reads as
// reconfiguring rather than cutting.
const PATTERN_FADE_BEATS = 2;
// The source pulses on every beat: dark for 30 ms, then back on.
const PULSE_OFF_S = 0.030;
// The half-beat of black that sets up the phrase: 16 beats per phrase, so
// 1/32 of it.
const BLACKOUT_PHRASE_FRAC = 1 / 32;
const BEATS_PER_PHRASE = 16;
// The unfolding: one mirror, then the count doubles at equal intervals until
// every mirror is lit, over two beats.
const UNFOLD_BEATS = 2.0;
// Dichroic phrases are RARE - about one phrase in six, tier 2 only.
const DICHROIC_CHANCE = 0.18;

// The drop, in beats from its own start: FOCUS on a point just in front of
// the camera (dazzle), then PARALLEL raking the whole room, boosted.
const DROP_FOCUS_BEATS = 2.0;
const DROP_SWEEP_BEATS = 8.0;
const DROP_TOTAL_BEATS = DROP_FOCUS_BEATS + DROP_SWEEP_BEATS;
const DROP_DAZZLE_GAIN = 1.3;
const DROP_FOCAL_DIST = 3.0;    // in front of the lens
const DROP_FOCAL_NDC_X = 0.58;  // to one side, clear of the cover
const DROP_FOCAL_NDC_Y = -0.12;

// FOCUS's point in the room, picked in SCREEN space and unprojected, exactly
// as beamwall.js does: it must be visible and it must not be on the cover,
// and neither is a statement about world coordinates.
const FOCUS_DIST = 9.5;        // from the camera
const FOCUS_MIN_Y = 1.0;
const FOCUS_R_MIN = 0.48;
const FOCUS_R_MAX = 0.78;
const FOCUS_EDGE = 0.88;
const FOCUS_STEP_R = 0.7;      // the notch: the point circles a little each beat

// PARALLEL's common direction. The target must never come near the view
// axis: 64 parallel beams converge on screen to their vanishing point, and
// with that point inside the frame the sheet reads as a radial BURST, which
// is what the first version looked like (yaw sweeping through 0 at a pitch
// of -0.16). Pitched down at 0.40 rad or more the vanishing point is below
// the frame and the beams stay a sheet of slanted lines that sweep together;
// the yaw swing takes them sideways across the room, one bar per sweep.
const PAR_YAW = 0.95;
const PAR_YAW_MAX = 1.25;
const PAR_PITCH = -0.50;
const PAR_PITCH_SWING = 0.10;

// The source's colour: a hue from the cover's vividColors at a fixed
// saturation, stepping to the next on every phrase. Every beam, impact and
// floor spot takes it; only a dichroic phrase tints the rows apart.
const LASER_SATURATION = 0.85;
const LASER_LIGHTNESS = 0.52;
const FALLBACK_COLORS = [0x30e0ff, 0xff3070, 0x80ff40];

// Synthetic band envelopes for the common case of no radiopi timeline, where
// director.bands is all zeros. Same block as beamwall.js / gallery.js.
const SYNTH_BASE = 0.10;
const SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7;
const SYNTH_PERIOD_MID = 3.1;
const SYNTH_PERIOD_HIGH = 6.3;
const SYNTH_BPM = 128;

const DEG = Math.PI / 180;
const _size = new THREE.Vector2();
const HALF_PI = Math.PI / 2;
const QUARTER_PI = Math.PI / 4;

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const SAFE_CHUNK = `
  float maSafeAtt(vec4 clipPos) {
    float a = safeZoneAttenuation(clipPos);
    return ${SAFE_FLOOR.toFixed(2)}
         + (a - ${SHARED_SAFE_FLOOR.toFixed(2)})
         * ${((1 - SAFE_FLOOR) / (1 - SHARED_SAFE_FLOOR)).toFixed(6)};
  }
`;

// The beams. One instance per beam, incident and reflected alike: the CPU
// writes origin, direction, length, gain and colour; the shader only has to
// billboard the quad. The billboard is laser-array.js's (pixel-floored
// width, side vector that never degenerates, guard against a beam pointing
// down the lens) with the width taken PER INSTANCE from aKind rather than
// from one uniform, since the two kinds differ by an order of magnitude.
// The guard is switched off during the drop where the dazzle is the point.
const beamVertexShader = `
  precision highp float;

  attribute vec3 aOrigin;
  attribute vec3 aDir;
  attribute float aLen;
  attribute float aGain;
  attribute vec3 aColor;
  attribute float aKind;     // 0: incident (source to mirror), 1: reflected

  uniform float uMasterGain;
  uniform float uGuardOff;
  uniform float uPixelScale;  // pixels per world unit at unit view depth
  uniform float uOuterPx;     // the reflected halo's full width, pixels

  varying float vSide;
  varying float vDist;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;
  varying float vKind;
  varying float vHaloPx;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  vec3 maPerp(vec3 n) {
    vec3 a = abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    return normalize(cross(n, a));
  }

  void main() {
    float t = position.y;                       // 0 at the origin, 1 at the end
    vec3 along = aOrigin + aDir * (t * aLen);

    vec3 toCam = cameraPosition - along;
    float camDist = length(toCam);
    toCam = camDist > 0.0001 ? toCam / camDist : vec3(0.0, 0.0, 1.0);
    float align = abs(dot(aDir, toCam));
    float guard = mix(0.30, 1.0, 1.0 - smoothstep(0.90, 0.999, align));
    guard = mix(guard, 1.0, uGuardOff);
    vec3 side = cross(aDir, toCam);
    float sideLen = length(side);
    side = sideLen > 0.0001 ? side / sideLen : maPerp(aDir);

    vec4 mv = modelViewMatrix * vec4(along, 1.0);
    float viewDepth = max(-mv.z, 0.001);
    float worldPerPx = viewDepth / uPixelScale;
    float outerPx = mix(${INCIDENT_PX.toFixed(2)}, uOuterPx, aKind);
    float worldHalf = mix(mix(${INCIDENT_HALF_NEAR.toFixed(4)}, ${INCIDENT_HALF_FAR.toFixed(4)}, t),
                          mix(${REFLECTED_HALF_NEAR.toFixed(4)}, ${REFLECTED_HALF_FAR.toFixed(4)}, t), aKind);
    float halfWidth = max(worldHalf, outerPx * 0.5 * worldPerPx);
    vec3 worldPos = along + side * (halfWidth * position.x);

    vSide = position.x;
    vDist = t * aLen;
    vColor = aColor;
    vKind = aKind;
    vHaloPx = halfWidth * 2.0 / worldPerPx;    // the quad's real width, pixels
    float fadeIn = mix(smoothstep(0.0, ${INCIDENT_FADE_IN.toFixed(2)}, t), 1.0, aKind);
    vGain = aGain * uMasterGain * guard * fadeIn;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = maSafeAtt(gl_Position);
  }
`;

// Two profiles. Reflected: laser-array.js's near-white core a few pixels
// wide inside a coloured halo. Incident: the colour alone, a quarter of the
// opacity, no core and no halo - a thread. Both with the extinction law a
// beam needs when it ENDS ON SOMETHING: no fade along its normalised length
// (an incident beam has to still be carrying light when it reaches its
// mirror), an exponential haze term in world units, and a hard close at
// BEAM_REACH for the reflected beams that find nothing.
const beamFragmentShader = `
  precision mediump float;

  uniform float uCorePx;
  uniform float uPulse;

  varying float vSide;
  varying float vDist;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;
  varying float vKind;
  varying float vHaloPx;

  ${laserClampChunk(PEAK_LUMINANCE)}

  void main() {
    float u = abs(vSide);
    float coreW = uCorePx / max(vHaloPx, 1.0);
    float core = 1.0 - smoothstep(0.0, coreW, u);
    float halo = (1.0 - u) * (1.0 - u);

    float atten = exp(-vDist * ${BEAM_EXT.toFixed(3)})
                * (1.0 - smoothstep(${BEAM_FADE_FROM.toFixed(1)}, ${BEAM_REACH.toFixed(1)}, vDist));

    vec3 hot = mix(vColor, vec3(1.0), ${BEAM_HOT_MIX.toFixed(2)});
    vec3 reflected = mix(vColor * halo * ${BEAM_HALO_W.toFixed(2)}, hot, core);
    vec3 incident = vColor * (halo * ${INCIDENT_OPACITY.toFixed(2)});
    vec3 col = mix(incident, reflected, vKind);
    col *= atten * uPulse * vGain * vSafeAtt;

    // AdditiveBlending is (SRC_ALPHA, ONE): all of the intensity lives in rgb
    // and alpha stays 1, or it would be squared.
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// The source: the point at the aperture, its halo and its anamorphic
// streak, on one billboard whose half-size is given in PIXELS (uFlareHalf:
// x the streak's half-length, y a little more than the halo's radius). The
// fragment works in pixels too. Drawn without depth so the housing's front
// face, which the aperture sits on, cannot clip it. The first version was a
// 210 px disc that drowned the box it was meant to sit on.
const flareVertexShader = `
  precision highp float;

  uniform float uPixelScale;
  uniform vec3 uSource;
  uniform vec2 uFlareHalf;

  varying vec2 vPx;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  void main() {
    vec4 mv = modelViewMatrix * vec4(uSource, 1.0);
    float depth = max(-mv.z, 0.001);
    vPx = position.xy * uFlareHalf;
    mv.xy += vPx * (depth / uPixelScale);
    gl_Position = projectionMatrix * mv;
    vSafeAtt = maSafeAtt(gl_Position);
  }
`;

const flareFragmentShader = `
  precision mediump float;

  uniform vec3 uColor;
  uniform float uPulse;
  uniform float uMasterGain;
  // highp in the vertex stage; a precision mismatch fails the link silently.
  uniform highp vec2 uFlareHalf;

  varying vec2 vPx;
  varying float vSafeAtt;

  ${laserClampChunk(PEAK_LUMINANCE)}

  void main() {
    float r2 = dot(vPx, vPx);
    float dotP = exp(-r2 / ${(2 * FLARE_POINT_SIGMA_PX * FLARE_POINT_SIGMA_PX).toFixed(2)});
    float halo = exp(-r2 / ${(2 * FLARE_HALO_SIGMA_PX * FLARE_HALO_SIGMA_PX).toFixed(2)}) * ${FLARE_HALO_GAIN.toFixed(2)};
    float taper = max(1.0 - abs(vPx.x) / uFlareHalf.x, 0.0);
    float streak = exp(-vPx.y * vPx.y / ${(2 * FLARE_STREAK_SIGMA_PX * FLARE_STREAK_SIGMA_PX).toFixed(2)})
                 * taper * taper * ${FLARE_STREAK_GAIN.toFixed(2)};
    vec3 col = mix(uColor, vec3(1.0), 0.85) * (dotP * 3.0) + uColor * (halo + streak);
    col *= uPulse * uMasterGain * vSafeAtt;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// The housing. Matt black metal under the same key light as the mirrors,
// with a tight specular; the chamfers (aGlass = 1 on their vertices) catch
// that light as a thin bright edge, and on the aperture's side they also
// catch the source's colour. The aperture is a dark disc in the middle of
// the front face (local +z, where lookAt points the box). Nothing here is
// ever lit grey: the flare is a separate additive quad in front of it.
const housingVertexShader = `
  precision highp float;

  attribute float aGlass;

  uniform float uFogDensity;

  varying vec3 vNormalW;
  varying vec3 vView;
  varying vec3 vLocal;
  varying float vBevel;
  varying float vFog;

  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vNormalW = mat3(modelMatrix) * normal;
    vView = cameraPosition - wp.xyz;
    vLocal = position;
    vBevel = aGlass;
    vec4 mv = viewMatrix * wp;
    float fd = uFogDensity * length(mv.xyz);
    vFog = exp(-fd * fd);
    gl_Position = projectionMatrix * mv;
  }
`;

const housingFragmentShader = `
  precision mediump float;

  uniform vec3 uColor;
  uniform float uPulse;

  varying vec3 vNormalW;
  varying vec3 vView;
  varying vec3 vLocal;
  varying float vBevel;
  varying float vFog;

  void main() {
    vec3 n = normalize(vNormalW);
    vec3 v = normalize(vView);
    vec3 L = normalize(vec3(-0.45, 0.80, 0.40));
    float lam = max(dot(n, L), 0.0);
    vec3 h = normalize(L + v);
    float spec = pow(max(dot(n, h), 0.0), 40.0);
    // The body is BLACK: the post chain (ACES, bloom) lifts dark tones, and
    // 0.04 x 1.2 plus a 0.06 specular read as mid grey in the frame.
    vec3 col = vec3(${HOUSING_BODY.toFixed(3)}) * (0.30 + 0.45 * lam) + vec3(0.02) * spec;
    // The chamfers: a lit edge, and the source's colour on the front ones.
    float front = smoothstep(0.0, ${(HOUSING_L / 2).toFixed(3)}, vLocal.z);
    col += vBevel * (vec3(0.10) * lam + vec3(0.16) * spec + uColor * (front * 0.40 * uPulse));
    // The aperture, on the front face only.
    float onFront = step(${(HOUSING_L / 2 - 0.002).toFixed(3)}, vLocal.z);
    float d = length(vLocal.xy);
    float aperture = onFront * (1.0 - smoothstep(${(APERTURE_R * 0.85).toFixed(3)}, ${APERTURE_R.toFixed(3)}, d));
    col = mix(col, vec3(0.006), aperture);
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// The contact shadow: a quad on the floor under the box, multiplied into
// whatever is there, dark under the box and fading out over SHADOW_MARGIN.
const shadowVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv * 2.0 - 1.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const shadowFragmentShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    float e = max(abs(vUv.x), abs(vUv.y));
    float s = 1.0 - smoothstep(0.55, 1.0, e);
    gl_FragColor = vec4(vec3(1.0 - ${SHADOW_DEPTH.toFixed(2)} * s), 1.0);
  }
`;

// Where a reflected beam meets the floor. The CPU already knows the hit
// point and the grazing direction; the quad is laid flat there, stretched
// along the beam's travel. reflection: the glossy floor's smear of the same
// hit toward the VIEWER (laser-cathedral.js's enrichment), halo only.
function makeImpactVertexShader(reflection) {
  return `
  precision highp float;

  attribute vec3 aHit;
  attribute vec3 aFwd;
  attribute float aStretch;
  attribute float aGain;
  attribute vec3 aColor;

  uniform float uMasterGain;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  void main() {
    ${reflection ? `
    vec2 toCam = cameraPosition.xz - aHit.xz;
    float tl = length(toCam);
    vec2 g = tl > 0.0001 ? toCam / tl : vec2(1.0, 0.0);
    vec3 fwd = vec3(g.x, 0.0, g.y);
    vec3 side = vec3(-fwd.z, 0.0, fwd.x);
    vec3 worldPos = aHit
                  + fwd * (position.x * ${IMPACT_SIZE.toFixed(2)} * ${REFLECTION_STRETCH.toFixed(1)})
                  + side * (position.z * ${IMPACT_SIZE.toFixed(2)} * 0.8);
    worldPos.y = 0.015;
    float gain = aGain * ${REFLECTION_GAIN.toFixed(2)};` : `
    vec3 side = vec3(-aFwd.z, 0.0, aFwd.x);
    vec3 worldPos = aHit
                  + aFwd * (position.x * ${IMPACT_SIZE.toFixed(2)} * aStretch)
                  + side * (position.z * ${IMPACT_SIZE.toFixed(2)});
    worldPos.y = 0.025;
    float gain = aGain;`}

    vUv = position.xz;
    vColor = aColor;
    vGain = gain * uMasterGain;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vGain *= maSafeAtt(gl_Position);
  }
`;
}

const impactFragmentShader = laserImpactFragmentShader({ hotMix: 0.80, peakLuminance: PEAK_LUMINANCE });

const reflectionFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${laserClampChunk(PEAK_LUMINANCE)}

  void main() {
    float r = length(vUv);
    float h1 = max(1.0 - r, 0.0);
    vec3 col = vColor * h1 * h1 * uPulse * vGain;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// The mirrors and their gimbals. Both are instanced on the SAME per-mirror
// normal the CPU wrote, so a plate cannot sit at an angle its beam does not
// leave at. panOnly: the fork, which turns about the vertical and never
// tilts. Without it: the plate, which does both.
//
// The plate carries its own light: the impact point where the incident beam
// lands (a dot of the source's colour, scaled by the mirror's activation),
// and the GLINT - when the reflected beam heads for the lens, the mirror
// shows the source itself, which is the brightest thing a mirror can show.
// Two lobes of it: a wide one (al^64, about ten degrees) so a plate that
// is merely NEAR to sending its beam at the viewer already brightens as it
// turns, and the hot one (al^256) for the flash itself. The glass also
// reflects a faked room (dark floor below, a dim grey-blue above), which is
// what makes a tilt readable on a plate that is otherwise black.
const FRAME_CHUNK = `
  // Right-handed frame whose +z is f. f is never vertical here (every
  // normal has a +z component), but the guard costs nothing.
  vec3 maRight(vec3 f) {
    vec3 up = abs(f.y) > 0.985 ? vec3(0.0, 0.0, 1.0) : vec3(0.0, 1.0, 0.0);
    return normalize(cross(up, f));
  }
`;

function makeMirrorVertexShader(panOnly) {
  return `
  precision highp float;

  attribute vec3 iPos;
  attribute vec3 iNormal;
  attribute float iLit;
  attribute float aGlass;

  uniform vec3 uSource;
  uniform float uFogDensity;

  varying vec3 vNormalW;
  varying vec3 vView;
  varying vec3 vToSource;
  varying float vGlass;
  varying vec2 vUv;
  varying float vFog;
  varying float vLit;
  varying float vGlint;
  varying float vSpec;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}
  ${FRAME_CHUNK}

  void main() {
    vec3 n = normalize(iNormal);
    ${panOnly ? `
    vec3 flat3 = vec3(n.x, 0.0, n.z);
    float fl = length(flat3);
    vec3 f = fl > 0.01 ? flat3 / fl : vec3(0.0, 0.0, 1.0);
    vec3 r = maRight(f);
    vec3 u = vec3(0.0, 1.0, 0.0);` : `
    vec3 f = n;
    vec3 r = maRight(f);
    vec3 u = cross(f, r);`}

    vec3 world = iPos + r * position.x + u * position.y + f * position.z;
    vNormalW = r * normal.x + u * normal.y + f * normal.z;
    vView = cameraPosition - world;
    vToSource = uSource - world;
    vGlass = aGlass;
    vUv = position.xy;
    vLit = iLit;

    // The glint: the exact reflection law again, this time asking whether
    // the reflected beam is heading for the camera.
    vec3 d = normalize(iPos - uSource);
    vec3 rr = d - 2.0 * dot(d, n) * n;
    vec3 toCam = normalize(cameraPosition - iPos);
    float al = max(dot(rr, toCam), 0.0);
    float al2 = al * al; float al4 = al2 * al2; float al8 = al4 * al4;
    float al16 = al8 * al8; float al32 = al16 * al16; float al64 = al32 * al32;
    vSpec = al64 * iLit;                          // about ten degrees
    vGlint = al64 * al64 * al64 * al64 * iLit;   // al^256: about five degrees

    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    float fd = uFogDensity * length(mv.xyz);
    gl_Position = projectionMatrix * mv;
    // One varying carries both multiplies: the haze, and the cover's own
    // attenuation - the grid sits directly behind the artwork.
    vFog = exp(-fd * fd) * maSafeAtt(gl_Position);
  }
`;
}

// No lights in the scene (RIG 'forbidden'), so the hardware carries its own
// shading, pitched to survive the fog's 44% at the grid: dark metal with a
// key light and a tight specular that catches as the plate turns, plus the
// laser's own colour grazing the frame from the source's side.
const mirrorFragmentShader = `
  precision mediump float;

  uniform vec3 uColor;
  uniform float uPulse;

  varying vec3 vNormalW;
  varying vec3 vView;
  varying vec3 vToSource;
  varying float vGlass;
  varying vec2 vUv;
  varying float vFog;
  varying float vLit;
  varying float vGlint;
  varying float vSpec;

  void main() {
    vec3 n = normalize(vNormalW);
    vec3 v = normalize(vView);
    vec3 L = normalize(vec3(-0.45, 0.80, 0.40));
    float lam = max(dot(n, L), 0.0);
    vec3 h = normalize(L + v);
    float spec = pow(max(dot(n, h), 0.0), 28.0);
    vec3 toSrc = normalize(vToSource);
    float lamSrc = max(dot(n, toSrc), 0.0);
    vec3 body = vec3(0.045, 0.05, 0.06) + vec3(0.22, 0.23, 0.25) * lam + vec3(0.40) * spec
              + uColor * (lamSrc * 0.35 * vLit * uPulse);

    // The glass: the faked room it reflects (dark below, dim above - the
    // tilt reads in that gradient), the impact dot where the incident beam
    // lands, and the two glint lobes in the source's colour pushed to white.
    vec3 rv = reflect(-v, n);
    float sky = smoothstep(-0.25, 0.75, rv.y);
    vec3 env = mix(vec3(0.012, 0.014, 0.02), vec3(0.10, 0.11, 0.15), sky);
    float r = length(vUv) / ${GLASS_HALF.toFixed(2)};
    float dot1 = exp(-r * r * 7.0);
    vec3 flash = mix(uColor, vec3(1.0), 0.6);
    vec3 glass = env
               + uColor * (dot1 * vLit * 4.0 * uPulse)
               + flash * ((vSpec * 0.45 + vGlint * 1.8) * uPulse);

    vec3 col = mix(body, glass, vGlass) * vFog;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- geometry helpers --------------------------------------------------------
// Boxes with real per-face normals and the aGlass attribute on every vertex,
// built by hand (BufferGeometryUtils is not in this bundle).

function pushBox(P, N, G, I, cx, cy, cz, sx, sy, sz, glass) {
  const hx = sx / 2, hy = sy / 2, hz = sz / 2;
  const faces = [
    [[0, 0, 1], [[-hx, -hy, hz], [hx, -hy, hz], [hx, hy, hz], [-hx, hy, hz]]],
    [[0, 0, -1], [[hx, -hy, -hz], [-hx, -hy, -hz], [-hx, hy, -hz], [hx, hy, -hz]]],
    [[1, 0, 0], [[hx, -hy, hz], [hx, -hy, -hz], [hx, hy, -hz], [hx, hy, hz]]],
    [[-1, 0, 0], [[-hx, -hy, -hz], [-hx, -hy, hz], [-hx, hy, hz], [-hx, hy, -hz]]],
    [[0, 1, 0], [[-hx, hy, hz], [hx, hy, hz], [hx, hy, -hz], [-hx, hy, -hz]]],
    [[0, -1, 0], [[-hx, -hy, -hz], [hx, -hy, -hz], [hx, -hy, hz], [-hx, -hy, hz]]]
  ];
  for (let f = 0; f < faces.length; f++) {
    const n = faces[f][0], c = faces[f][1];
    const base = P.length / 3;
    for (let i = 0; i < 4; i++) {
      P.push(cx + c[i][0], cy + c[i][1], cz + c[i][2]);
      N.push(n[0], n[1], n[2]);
      G.push(glass);
    }
    I.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}

function finishGeometry(P, N, G, I, instanced) {
  const g = instanced ? new THREE.InstancedBufferGeometry() : new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(P), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(N), 3));
  g.setAttribute('aGlass', new THREE.BufferAttribute(new Float32Array(G), 1));
  g.setIndex(I);
  return g;
}

// The plate: a black frame with the reflective face set into its front.
// Local +z is the mirror's normal.
function buildPlateGeometry() {
  const P = [], N = [], G = [], I = [];
  pushBox(P, N, G, I, 0, 0, 0, PLATE_HALF * 2, PLATE_HALF * 2, PLATE_DEPTH, 0);
  pushBox(P, N, G, I, 0, 0, PLATE_DEPTH / 2 + 0.004, GLASS_HALF * 2, GLASS_HALF * 2, 0.008, 1);
  return finishGeometry(P, N, G, I, true);
}

// The gimbal's outer ring: a fork whose arms carry the tilt axis through the
// plate's sides, a back bar, and the stem down to the bracket. Pan only.
function buildForkGeometry() {
  const P = [], N = [], G = [], I = [];
  const ax = PLATE_HALF + 0.045;
  pushBox(P, N, G, I, -ax, 0, -0.10, 0.055, 0.13, 0.30, 0);
  pushBox(P, N, G, I, ax, 0, -0.10, 0.055, 0.13, 0.30, 0);
  pushBox(P, N, G, I, 0, 0, -0.26, ax * 2 + 0.055, 0.11, 0.06, 0);
  pushBox(P, N, G, I, 0, 0, -0.335, 0.09, 0.09, 0.09, 0);
  return finishGeometry(P, N, G, I, true);
}

// The chassis: the part that does NOT move. A rail per row, two uprights,
// and a bracket behind every mirror for its fork to turn on. Baked once in
// world coordinates.
function buildChassisGeometry(mirrors, n, spacing) {
  const P = [], N = [], G = [], I = [];
  const halfW = ((n - 1) * spacing) / 2 + 0.55;
  const z = GRID_Z - 0.52;
  const h0 = n / 2 - 1;
  const holeHalf = spacing * 1.5;         // the 2 x 2 hole, with its margin
  for (let r = 0; r < n; r++) {
    const y = GRID_CY + (r - (n - 1) / 2) * spacing;
    if (r === h0 || r === h0 + 1) {
      // The two rails that would cross the hole stop at its edges.
      const w = halfW - holeHalf;
      pushBox(P, N, G, I, -(holeHalf + w / 2), y, z, w, 0.10, 0.10, 0);
      pushBox(P, N, G, I, (holeHalf + w / 2), y, z, w, 0.10, 0.10, 0);
    } else {
      pushBox(P, N, G, I, 0, y, z, halfW * 2, 0.10, 0.10, 0);
    }
  }
  const topY = GRID_CY + ((n - 1) / 2) * spacing + 0.5;
  const botY = GRID_CY - ((n - 1) / 2) * spacing - 0.5;
  pushBox(P, N, G, I, -halfW, (topY + botY) / 2, z, 0.14, topY - botY, 0.14, 0);
  pushBox(P, N, G, I, halfW, (topY + botY) / 2, z, 0.14, topY - botY, 0.14, 0);
  pushBox(P, N, G, I, 0, topY, z, halfW * 2, 0.12, 0.12, 0);
  pushBox(P, N, G, I, 0, botY, z, halfW * 2, 0.12, 0.12, 0);
  for (let i = 0; i < mirrors.length; i++) {
    const m = mirrors[i];
    pushBox(P, N, G, I, m.pos.x, m.pos.y, GRID_Z - 0.44, 0.10, 0.10, 0.16, 0);
  }
  return finishGeometry(P, N, G, I, false);
}

function pushQuad(P, N, G, I, a, b, c, d, n, g) {
  const base = P.length / 3;
  P.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], d[0], d[1], d[2]);
  for (let i = 0; i < 4; i++) { N.push(n[0], n[1], n[2]); G.push(g); }
  I.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

// The laser housing: a box centred at the origin, +z toward the grid (the
// front face carries the aperture), its four top edges chamfered. The
// chamfer vertices carry aGlass = 1, which the housing shader reads as "a
// bevel". Built by hand so the chamfers have their own 45-degree normals -
// a flat-shaded box has no edge for a key light to draw.
function buildHousingGeometry() {
  const P = [], N = [], G = [], I = [];
  const hx = HOUSING_W / 2, hy = HOUSING_H / 2, hz = HOUSING_L / 2, c = HOUSING_CHAMFER;
  const ty = hy - c;                   // where the sides stop and the chamfers begin
  const R2 = Math.SQRT1_2;
  // Sides, up to the chamfer line (outward winding, seen from outside).
  pushQuad(P, N, G, I, [-hx, -hy, hz], [hx, -hy, hz], [hx, ty, hz], [-hx, ty, hz], [0, 0, 1], 0);
  pushQuad(P, N, G, I, [hx, -hy, -hz], [-hx, -hy, -hz], [-hx, ty, -hz], [hx, ty, -hz], [0, 0, -1], 0);
  pushQuad(P, N, G, I, [hx, -hy, hz], [hx, -hy, -hz], [hx, ty, -hz], [hx, ty, hz], [1, 0, 0], 0);
  pushQuad(P, N, G, I, [-hx, -hy, -hz], [-hx, -hy, hz], [-hx, ty, hz], [-hx, ty, -hz], [-1, 0, 0], 0);
  pushQuad(P, N, G, I, [-hx, -hy, -hz], [hx, -hy, -hz], [hx, -hy, hz], [-hx, -hy, hz], [0, -1, 0], 0);
  // The top, inset by the chamfer.
  pushQuad(P, N, G, I, [-hx + c, hy, hz - c], [hx - c, hy, hz - c], [hx - c, hy, -hz + c], [-hx + c, hy, -hz + c], [0, 1, 0], 0);
  // The four chamfers.
  pushQuad(P, N, G, I, [-hx, ty, hz], [hx, ty, hz], [hx - c, hy, hz - c], [-hx + c, hy, hz - c], [0, R2, R2], 1);
  pushQuad(P, N, G, I, [hx, ty, -hz], [-hx, ty, -hz], [-hx + c, hy, -hz + c], [hx - c, hy, -hz + c], [0, R2, -R2], 1);
  pushQuad(P, N, G, I, [hx, ty, hz], [hx, ty, -hz], [hx - c, hy, -hz + c], [hx - c, hy, hz - c], [R2, R2, 0], 1);
  pushQuad(P, N, G, I, [-hx, ty, -hz], [-hx, ty, hz], [-hx + c, hy, hz - c], [-hx + c, hy, -hz + c], [-R2, R2, 0], 1);
  // Adjacent chamfers share the edge from the box corner to the top's
  // corner, so there is no gap to close.
  return finishGeometry(P, N, G, I, false);
}

// Compiled once and NEVER released: three.js frees a program the moment its
// last material is disposed, and this visualizer would otherwise recompile
// five programs on every appearance in the rotation (seconds, on the Pi -
// see beamwall.js's MATERIALS and nebula.js's VOLUME_MATERIALS). The uniform
// objects are cached with the materials, so the visualizer ADOPTS them and
// resets in _resetUniforms whatever update() does not write every frame.
let MATERIALS = null;

function sharedMaterials() {
  if (MATERIALS) return MATERIALS;
  const uniforms = Object.assign({}, safeZoneUniforms, {
    uPulse: { value: 1 },
    uMasterGain: { value: 1 },
    uGuardOff: { value: 0 },
    uPixelScale: { value: 1 },
    uOuterPx: { value: REFLECTED_PX },
    uCorePx: { value: REFLECTED_CORE_PX },
    uFogDensity: { value: FOG_DENSITY },
    uSource: { value: new THREE.Vector3(SOURCE_FALLBACK[0], SOURCE_FALLBACK[1], SOURCE_FALLBACK[2]) },
    uFlareHalf: { value: new THREE.Vector2(96, FLARE_HALF_H_PX) },
    uColor: { value: new THREE.Color(FALLBACK_COLORS[0]) }
  });
  const additive = {
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true
  };
  MATERIALS = {
    uniforms: uniforms,
    beam: new THREE.ShaderMaterial(Object.assign({
      uniforms: uniforms, vertexShader: beamVertexShader, fragmentShader: beamFragmentShader
    }, additive)),
    impact: new THREE.ShaderMaterial(Object.assign({
      uniforms: uniforms, vertexShader: makeImpactVertexShader(false), fragmentShader: impactFragmentShader,
      // The flat quad's winding and the re-basing onto (fwd, side) flip
      // handedness: seen from above every impact is a back face without
      // this (beamwall.js and ravesign.js learned it the hard way).
      side: THREE.DoubleSide
    }, additive)),
    reflection: new THREE.ShaderMaterial(Object.assign({
      uniforms: uniforms, vertexShader: makeImpactVertexShader(true), fragmentShader: reflectionFragmentShader,
      side: THREE.DoubleSide
    }, additive)),
    flare: new THREE.ShaderMaterial(Object.assign({
      uniforms: uniforms, vertexShader: flareVertexShader, fragmentShader: flareFragmentShader
    }, additive, { depthTest: false })),
    plate: new THREE.ShaderMaterial({
      uniforms: uniforms, vertexShader: makeMirrorVertexShader(false), fragmentShader: mirrorFragmentShader,
      transparent: false, depthWrite: true, depthTest: true
    }),
    fork: new THREE.ShaderMaterial({
      uniforms: uniforms, vertexShader: makeMirrorVertexShader(true), fragmentShader: mirrorFragmentShader,
      transparent: false, depthWrite: true, depthTest: true
    }),
    housing: new THREE.ShaderMaterial({
      uniforms: uniforms, vertexShader: housingVertexShader, fragmentShader: housingFragmentShader,
      transparent: false, depthWrite: true, depthTest: true
    }),
    shadow: new THREE.ShaderMaterial({
      vertexShader: shadowVertexShader, fragmentShader: shadowFragmentShader,
      transparent: true, blending: THREE.MultiplyBlending, premultipliedAlpha: true,
      depthWrite: false, depthTest: true
    }),
    chassis: new THREE.MeshBasicMaterial({ color: CHASSIS_COLOR, fog: true })
  };
  return MATERIALS;
}

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// Direction from (yaw, pitch) about +z: straight out of the grid, at the room.
function anglesTo(out, yaw, pitch) {
  const cp = Math.cos(pitch);
  return out.set(Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp);
}

export class MirrorArrayVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // ?mirrormode=parallel|focus|fan|wave|spiral pins one choreography.
    const forced = urlParam('mirrormode');
    this._forcedPattern = forced != null && PATTERN_NAMES[forced] !== undefined
      ? PATTERN_NAMES[forced] : null;
    // ?mirrorunfold=1 fires the unfolding at start and again every 8 beats,
    // so it can be watched without waiting for a phrase.
    this._debugUnfold = urlParam('mirrorunfold') === '1';
    // ?mirrorsingle=1 lights ONE mirror (the first the source sweeps), so
    // the reflection law can be checked on a single pair of beams.
    this._single = urlParam('mirrorsingle') === '1';
    // ?mirrordichro=1 pins the dichroic filter on every phrase.
    this._debugDichroic = urlParam('mirrordichro') === '1';
    // ?mirrorprobe=1 publishes the live state on window.__mirrorarray.
    this._probe = urlParam('mirrorprobe') === '1';
    // ?mirroraa=<n> overrides the tier's MSAA sample count (0 = direct).
    const aa = parseInt(urlParam('mirroraa'), 10);
    this._samples = Number.isFinite(aa) ? aa : TIER_LAYOUT[this._tier].samples;
    this._options = options || {};
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.fov = CAMERA_FOV;
    camera.updateProjectionMatrix();
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    // Everything lives in the inner scene: the shared one when rendering
    // directly, a private one when going through the MSAA target.
    this._direct = this._samples === 0;
    this._inner = this._direct ? scene : new THREE.Scene();
    this._inner.fog = new THREE.FogExp2(0x000000, FOG_DENSITY);

    const layout = TIER_LAYOUT[this._tier];
    this._layout = layout;
    this._n = layout.n;
    this._spacing = layout.span / (layout.n - 1);
    this._source = new THREE.Vector3();
    this._v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    this._placeSource();

    const mats = sharedMaterials();
    this._uniforms = mats.uniforms;
    this._mats = mats;

    // The beam leaves the housing EMITTER_OFFSET along its aim (at the
    // grid's centre), and that point - not the housing's centre - is the
    // origin every incident direction is measured from, the point the glint
    // is computed against, and where the flare sits.
    this._aimFlat = new THREE.Vector3(0 - this._source.x, 0, GRID_Z - this._source.z).normalize();
    this._emitOrigin = this._aimFlat.clone().multiplyScalar(EMITTER_OFFSET).add(this._source);
    this._resetUniforms();
    this._buildMirrorList();
    this._buildFloor();
    this._buildBackdrop();
    this._buildBeams();
    if (layout.impacts) this._buildImpacts();
    this._buildMirrors();
    this._buildChassis();
    this._buildHousing();
    if (!this._direct) this._buildTarget();

    // --- choreography state ---
    this._time = 0;
    this._step = 0;
    this._sinceStep = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._pat = this._forcedPattern != null ? this._forcedPattern : PAT_PARALLEL;
    this._patPrev = this._pat;
    this._patFade = 1;
    this._patOrder = this._shuffledPatterns();
    this._patCursor = 0;
    this._phraseCount = 0;
    this._dominantNext = PAT_FOCUS;   // the opening phrase is PARALLEL
    this._unfoldT = 99;              // beats since the unfolding began; >= UNFOLD_BEATS = done
    this._unfoldDueAtStep = this._debugUnfold ? 0 : -1;
    this._dropT = -1;
    this._burst = 0;
    this._dazzle = 0;
    this._spread = 1;
    this._litCount = 0;
    // The opening (bands.low, and the drop's burst) is LATCHED at each
    // notch: a mirror that followed a smoothed envelope between notches
    // would slide, and the hold is what makes the motion read as motors.
    this._bmPrev = 1;
    this._bmCur = 1;
    this._dichroic = false;
    this._colorIndex = 0;
    this._hues = [];
    this._focal = new THREE.Vector3(2.5, 3.0, -1.0);
    this._dropFocal = new THREE.Vector3();
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._baseColor = new THREE.Color(FALLBACK_COLORS[0]);
    this._rowColors = [];
    for (let r = 0; r < this._n; r++) this._rowColors.push(new THREE.Color());
    this._applyPalette(null);
    this._pickFocal();
    if (this._debugUnfold) this._unfoldT = 0;
    // One pass so the first frame already shows the grid in place.
    this._stepChoreography(0);
  }

  // --- the source ------------------------------------------------------------

  // The housing's centre: the ray through (SOURCE_NDC_X, SOURCE_NDC_Y) of
  // the frame, met with the plane y = SOURCE_Y. The camera is already posed
  // and its aspect set by scene-manager when init runs, so the unprojection
  // sees the real frame.
  _placeSource() {
    const cam = this._camera;
    const v = this._v[5];
    // init has just posed the camera; its world matrix is still the previous
    // composition's until a render, and unproject reads THAT matrix.
    cam.updateMatrixWorld(true);
    v.set(SOURCE_NDC_X, SOURCE_NDC_Y, 0.5).unproject(cam).sub(cam.position).normalize();
    if (v.y < -1e-3) {
      const t = (SOURCE_Y - cam.position.y) / v.y;
      if (t > 2 && t < 40) {
        this._source.copy(cam.position).addScaledVector(v, t);
        return;
      }
    }
    this._source.set(SOURCE_FALLBACK[0], SOURCE_FALLBACK[1], SOURCE_FALLBACK[2]);
  }

  // --- the mirrors -------------------------------------------------------------

  // The list of mirrors, in the order the source sweeps them: row by row from
  // the bottom (nearest the source on the floor), each row alternating
  // direction so the sweep is one continuous path. k is that order; the
  // unfolding lights mirror k at log2(k+1)/log2(N) of its two beats, which is
  // what makes the count double at equal intervals.
  _buildMirrorList() {
    const n = this._n;
    const h0 = n / 2 - 1;         // the 2 x 2 hole: columns/rows h0 and h0+1
    const mirrors = [];
    for (let row = 0; row < n; row++) {
      for (let c = 0; c < n; c++) {
        const col = row % 2 === 0 ? c : n - 1 - c;
        if ((col === h0 || col === h0 + 1) && (row === h0 || row === h0 + 1)) continue;
        mirrors.push({
          col: col, row: row,
          cx: (col / (n - 1)) * 2 - 1,
          cy: (row / (n - 1)) * 2 - 1,
          pos: new THREE.Vector3((col - (n - 1) / 2) * this._spacing,
                                 GRID_CY + (row - (n - 1) / 2) * this._spacing, GRID_Z),
          toSource: new THREE.Vector3(),
          incident: new THREE.Vector3(),
          incidentLen: 0,
          normal: new THREE.Vector3(0, 0, 1),
          reflected: new THREE.Vector3(0, 0, 1),
          active: 0,
          unfoldAt: 0
        });
      }
    }
    const N = mirrors.length;
    for (let k = 0; k < N; k++) {
      const m = mirrors[k];
      m.toSource.copy(this._emitOrigin).sub(m.pos);
      m.incidentLen = m.toSource.length();
      m.toSource.normalize();
      m.incident.copy(m.toSource).negate();
      m.unfoldAt = N > 1 ? (Math.log(k + 1) / Math.log(N)) * UNFOLD_BEATS : 0;
    }
    this._mirrors = mirrors;
    this._count = N;
  }

  // --- geometry --------------------------------------------------------------

  _buildFloor() {
    this._floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    this._floorMaterial = new THREE.MeshBasicMaterial({ color: FLOOR_COLOR });
    this._floor = new THREE.Mesh(this._floorGeometry, this._floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    this._inner.add(this._floor);
  }

  _buildBackdrop() {
    this._backGeometry = new THREE.PlaneGeometry(60, 30);
    this._backMaterial = new THREE.MeshBasicMaterial({ color: BACKDROP_COLOR });
    this._backdrop = new THREE.Mesh(this._backGeometry, this._backMaterial);
    this._backdrop.position.set(0, 10, GRID_Z - 1.4);
    this._inner.add(this._backdrop);
  }

  _dynamicAttribute(size, itemSize) {
    const attr = new THREE.InstancedBufferAttribute(new Float32Array(size * itemSize), itemSize);
    attr.setUsage(THREE.DynamicDrawUsage);
    return attr;
  }

  // Instance k < count: the incident beam to mirror k. Instance count + k:
  // the beam reflected off mirror k.
  _buildBeams() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    const n = this._count * 2;
    this._bOrigin = this._dynamicAttribute(n, 3);
    this._bDir = this._dynamicAttribute(n, 3);
    this._bLen = this._dynamicAttribute(n, 1);
    this._bGain = this._dynamicAttribute(n, 1);
    this._bColor = this._dynamicAttribute(n, 3);
    const kind = new Float32Array(n);
    for (let k = this._count; k < n; k++) kind[k] = 1;
    geometry.setAttribute('aKind', new THREE.InstancedBufferAttribute(kind, 1));
    geometry.setAttribute('aOrigin', this._bOrigin);
    geometry.setAttribute('aDir', this._bDir);
    geometry.setAttribute('aLen', this._bLen);
    geometry.setAttribute('aGain', this._bGain);
    geometry.setAttribute('aColor', this._bColor);
    geometry.instanceCount = n;
    this._beamGeometry = geometry;
    this._beams = new THREE.Mesh(geometry, this._mats.beam);
    this._beams.frustumCulled = false;
    this._inner.add(this._beams);
  }

  _buildImpacts() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    // One instance per mirror, plus one: the spot on the floor in front of
    // the source's aperture (written every frame like the others, so it
    // follows the colour).
    const n = this._count + 1;
    this._iHit = this._dynamicAttribute(n, 3);
    this._iFwd = this._dynamicAttribute(n, 3);
    this._iStretch = this._dynamicAttribute(n, 1);
    this._iGain = this._dynamicAttribute(n, 1);
    this._iColor = this._dynamicAttribute(n, 3);
    geometry.setAttribute('aHit', this._iHit);
    geometry.setAttribute('aFwd', this._iFwd);
    geometry.setAttribute('aStretch', this._iStretch);
    geometry.setAttribute('aGain', this._iGain);
    geometry.setAttribute('aColor', this._iColor);
    geometry.instanceCount = n;
    this._impactGeometry = geometry;
    this._impacts = new THREE.Mesh(geometry, this._mats.impact);
    this._impacts.frustumCulled = false;
    this._inner.add(this._impacts);
    if (this._layout.reflection) {
      // Shares the geometry outright: the same hits, smeared toward the lens.
      this._reflection = new THREE.Mesh(geometry, this._mats.reflection);
      this._reflection.frustumCulled = false;
      this._inner.add(this._reflection);
    }
  }

  // Two instanced draws: the plates (pan and tilt) and the forks (pan only).
  // Both read the same iNormal, written once per frame.
  _buildMirrors() {
    const n = this._count;
    this._mPos = this._dynamicAttribute(n, 3);
    this._mNormal = this._dynamicAttribute(n, 3);
    this._mLit = this._dynamicAttribute(n, 1);
    for (let k = 0; k < n; k++) {
      const p = this._mirrors[k].pos;
      this._mPos.setXYZ(k, p.x, p.y, p.z);
    }
    this._mPos.needsUpdate = true;

    this._plateGeometry = buildPlateGeometry();
    this._plateGeometry.setAttribute('iPos', this._mPos);
    this._plateGeometry.setAttribute('iNormal', this._mNormal);
    this._plateGeometry.setAttribute('iLit', this._mLit);
    this._plateGeometry.instanceCount = n;
    this._plates = new THREE.Mesh(this._plateGeometry, this._mats.plate);
    this._plates.frustumCulled = false;
    this._inner.add(this._plates);

    this._forkGeometry = buildForkGeometry();
    this._forkGeometry.setAttribute('iPos', this._mPos);
    this._forkGeometry.setAttribute('iNormal', this._mNormal);
    this._forkGeometry.setAttribute('iLit', this._mLit);
    this._forkGeometry.instanceCount = n;
    this._forks = new THREE.Mesh(this._forkGeometry, this._mats.fork);
    this._forks.frustumCulled = false;
    this._inner.add(this._forks);
  }

  _buildChassis() {
    this._chassisGeometry = buildChassisGeometry(this._mirrors, this._n, this._spacing);
    this._chassis = new THREE.Mesh(this._chassisGeometry, this._mats.chassis);
    this._chassis.frustumCulled = false;
    this._inner.add(this._chassis);
  }

  _buildHousing() {
    this._housingGeometry = buildHousingGeometry();
    this._housing = new THREE.Mesh(this._housingGeometry, this._mats.housing);
    this._housing.position.copy(this._source);
    // Yawed at the grid's centre (lookAt points local +z, the front face,
    // at the target); the target is taken at the housing's own height so a
    // box on the floor stays flat on it.
    this._housing.lookAt(0, this._source.y, GRID_Z);
    this._inner.add(this._housing);
    // The contact shadow, a child so it follows the yaw.
    this._shadowGeometry = new THREE.PlaneGeometry(HOUSING_W + 2 * SHADOW_MARGIN, HOUSING_L + 2 * SHADOW_MARGIN);
    this._shadow = new THREE.Mesh(this._shadowGeometry, this._mats.shadow);
    this._shadow.rotation.x = -Math.PI / 2;
    this._shadow.position.set(0, -HOUSING_H / 2 + 0.003, 0);
    this._housing.add(this._shadow);
    // The flare: a billboard at the aperture, drawn last and without depth.
    this._flareGeometry = new THREE.PlaneGeometry(2, 2);
    this._flare = new THREE.Mesh(this._flareGeometry, this._mats.flare);
    this._flare.frustumCulled = false;
    this._flare.renderOrder = 10;
    this._inner.add(this._flare);
  }

  // The private MSAA target and the full-frame quad that shows it (the
  // metronome.js / ferro.js pattern): the inner scene is rendered from the
  // quad's onBeforeRender with the SAME camera, into a target sized to the
  // drawing buffer, and the quad samples it 1:1. No tone mapping is applied
  // inside a render target, so the composer's bloom and OutputPass see the
  // same linear values as for any other visualizer. HalfFloat at tier 2 so
  // the additive beams keep their sums above 1.0 for the bloom.
  _buildTarget() {
    const self = this;
    const rtType = this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this._rt = new THREE.WebGLRenderTarget(4, 4, {
      samples: this._samples, type: rtType, depthBuffer: true, stencilBuffer: false,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false
    });
    this._quadMat = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this._rt.texture } },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }`,
      fragmentShader: `
        uniform sampler2D tScene;
        varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(tScene, vUv).rgb, 1.0); }`,
      depthTest: false, depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._quadMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -3000;
    this._scene.add(this._quad);
    this._quad.onBeforeRender = function (renderer, outerScene, cam) {
      renderer.getDrawingBufferSize(_size);
      if (self._rt.width !== _size.x || self._rt.height !== _size.y) self._rt.setSize(_size.x, _size.y);
      const prevTarget = renderer.getRenderTarget();
      const prevXr = renderer.xr.enabled;
      renderer.xr.enabled = false;
      renderer.setRenderTarget(self._rt);
      renderer.clear(true, true, false);
      renderer.render(self._inner, cam);
      renderer.setRenderTarget(prevTarget);
      renderer.xr.enabled = prevXr;
    };
  }

  _resetUniforms() {
    const u = this._uniforms;
    u.uPulse.value = 1;
    u.uMasterGain.value = 1;
    u.uGuardOff.value = 0;
    u.uOuterPx.value = REFLECTED_PX;
    u.uCorePx.value = REFLECTED_CORE_PX;
    u.uFogDensity.value = FOG_DENSITY;
    u.uSource.value.copy(this._emitOrigin);
  }

  // --- palette ---------------------------------------------------------------

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : FALLBACK_COLORS;
    this._hues = [];
    for (let i = 0; i < vivid.length; i++) {
      this._scratch.set(vivid[i]).getHSL(this._hsl);
      this._hues.push(this._hsl.h);
    }
    if (!this._hues.length) this._hues.push(0.55);
    this._setColor();
  }

  _setColor() {
    const h = this._hues[this._colorIndex % this._hues.length];
    this._baseColor.setHSL(h, LASER_SATURATION, LASER_LIGHTNESS);
    this._uniforms.uColor.value.copy(this._baseColor);
    // The dichroic filter: each row of mirrors tints its reflection a step
    // further round the wheel. The incident beam stays the source's colour.
    for (let r = 0; r < this._n; r++) {
      this._rowColors[r].setHSL((h + r / this._n) % 1, LASER_SATURATION, LASER_LIGHTNESS);
    }
  }

  // --- choreography ----------------------------------------------------------

  // The SECONDARY patterns only (FAN, WAVE, SPIRAL). PARALLEL and FOCUS are
  // the show - 64 beams moving as one - and take every other phrase by
  // construction (_changePattern), not by the luck of a shuffle.
  _shuffledPatterns() {
    const order = [PAT_FAN, PAT_WAVE, PAT_SPIRAL];
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = order[i]; order[i] = order[j]; order[j] = t;
    }
    return order;
  }

  // FOCUS's point: candidates on a ring around the cover in screen space,
  // the first that is off the cover and inside the frame wins, unprojected
  // FOCUS_DIST in front of the camera. Never inside the safe zone.
  _pickFocal() {
    if (!this._camera) return;
    // R19: the ring and its keep-out are the stage's, not the cover's.
    const cx = safeZoneUniforms.uStageCenter.value.x;
    const cy = safeZoneUniforms.uStageCenter.value.y;
    const hx = safeZoneUniforms.uStageHalfSize.value.x + safeZoneUniforms.uSafeFeather.value;
    const hy = safeZoneUniforms.uStageHalfSize.value.y + safeZoneUniforms.uSafeFeather.value;
    const aspect = safeZoneUniforms.uAspect.value || 1;
    const start = Math.floor(Math.random() * 12);
    for (let k = 0; k < 12; k++) {
      const ang = ((start + k) % 12) * 30 * DEG;
      const r = FOCUS_R_MIN + Math.random() * (FOCUS_R_MAX - FOCUS_R_MIN);
      const sx = cx + Math.cos(ang) * r * 1.35;
      const sy = cy + Math.sin(ang) * r;
      if (Math.abs(sx - cx) <= hx && Math.abs(sy - cy) <= hy) continue;
      const ndcX = sx / aspect;
      if (Math.abs(ndcX) > FOCUS_EDGE || Math.abs(sy) > FOCUS_EDGE) continue;
      if (this._unprojectTo(ndcX, sy, FOCUS_DIST, FOCUS_MIN_Y, this._focal)) return;
    }
    const sx = cx - hx - 0.14;
    this._unprojectTo(Math.max(-FOCUS_EDGE, sx / aspect), cy, FOCUS_DIST, FOCUS_MIN_Y, this._focal);
  }

  // The drop's point: just in front of the lens, to one side so the
  // convergence lands beside the cover rather than on it.
  _pickDropFocal() {
    if (!this._camera) return;
    // R19: beside the stage's block, not the cover's.
    const cx = safeZoneUniforms.uStageCenter.value.x;
    const hx = safeZoneUniforms.uStageHalfSize.value.x + safeZoneUniforms.uSafeFeather.value;
    const aspect = safeZoneUniforms.uAspect.value || 1;
    const side = Math.random() < 0.5 ? -1 : 1;
    let ndcX = side * DROP_FOCAL_NDC_X;
    const edge = (cx + side * hx) / aspect;       // the cover's edge on that side
    if (side > 0 ? ndcX < edge + 0.08 : ndcX > edge - 0.08) ndcX = edge + side * 0.08;
    ndcX = Math.max(-0.9, Math.min(0.9, ndcX));
    this._unprojectTo(ndcX, DROP_FOCAL_NDC_Y, DROP_FOCAL_DIST, -99, this._dropFocal);
  }

  _unprojectTo(ndcX, ndcY, dist, minY, out) {
    const v = this._v[5];
    this._camera.updateMatrixWorld(true);   // see _placeSource
    v.set(ndcX, ndcY, 0.5).unproject(this._camera);
    v.sub(this._camera.position).normalize().multiplyScalar(dist).add(this._camera.position);
    if (v.y < minY) return false;
    out.copy(v);
    return true;
  }

  // The reflected direction pattern `pat` asks of mirror m at step s. Every
  // choreography is written as a TARGET for the reflection; the normal is
  // then the bisector between the direction to the source and that target,
  // which is the only normal that sends the beam there.
  _targetDir(m, s, pat, out) {
    const bm = s < this._step ? this._bmPrev : this._bmCur;
    if (pat === PAT_PARALLEL) {
      // Every mirror on the SAME vector: one bar is one sweep across the
      // room (yaw: A, 0, -A, 0 over four notches), the pitch breathing over
      // two bars - always well below the view axis, see PAR_PITCH.
      let yaw = PAR_YAW * Math.cos(s * HALF_PI) * bm;
      if (yaw > PAR_YAW_MAX) yaw = PAR_YAW_MAX; else if (yaw < -PAR_YAW_MAX) yaw = -PAR_YAW_MAX;
      const pitch = PAR_PITCH + PAR_PITCH_SWING * Math.sin(s * QUARTER_PI) * bm;
      return anglesTo(out, yaw, pitch);
    }
    if (pat === PAT_FOCUS) {
      // Through one point of the room, the point itself notching round a
      // small circle so the whole cone of beams steps with the beat.
      const f = this._v[4].copy(this._focal);
      f.x += Math.cos(s * HALF_PI) * FOCUS_STEP_R;
      f.y += Math.sin(s * HALF_PI) * FOCUS_STEP_R * 0.5;
      return out.copy(f).sub(m.pos).normalize();
    }
    if (pat === PAT_DROP_FOCUS) {
      return out.copy(this._dropFocal).sub(m.pos).normalize();
    }
    if (pat === PAT_FAN) {
      // The reflections open as a regular fan, the opening stepping each
      // beat: a full breath per bar.
      const amt = (0.35 + 0.65 * Math.abs(Math.sin(s * QUARTER_PI))) * bm;
      return anglesTo(out, m.cx * 0.55 * amt, m.cy * 0.34 * amt - 0.14);
    }
    if (pat === PAT_WAVE) {
      // One wave of tilt crossing the columns, a quarter period per beat.
      const pitch = -0.12 + 0.30 * bm * Math.sin(m.cx * Math.PI - s * HALF_PI);
      return anglesTo(out, m.cx * 0.16, pitch);
    }
    // SPIRAL - the angle turns with the distance from the centre and with the
    // step: the reflections wind into a vortex.
    const rho = Math.sqrt(m.cx * m.cx + m.cy * m.cy);
    const theta = Math.atan2(m.cy, m.cx) + s * QUARTER_PI + rho * 2.4;
    const R = 0.50 * rho * bm;
    return anglesTo(out, R * Math.cos(theta), R * Math.sin(theta) - 0.12);
  }

  // The normal for (m, s, pat): the bisector of toSource and the target.
  _normalAt(m, s, pat, out) {
    const t = this._targetDir(m, s, pat, this._v[0]);
    out.copy(m.toSource).add(t);
    const l = out.length();
    return l > 1e-5 ? out.multiplyScalar(1 / l) : out.set(0, 0, 1);
  }

  // Notched: step-1 and step, eased over MOVE_MS then held; and both
  // patterns during a crossfade.
  _stepNormal(m, pat, stepMix, out) {
    const a = this._normalAt(m, this._step - 1, pat, this._v[1]);
    const b = this._normalAt(m, this._step, pat, this._v[2]);
    out.copy(a).lerp(b, stepMix);
    const l = out.length();
    return l > 1e-5 ? out.multiplyScalar(1 / l) : out.copy(b);
  }

  _mirrorNormal(m, stepMix, out) {
    const live = this._dropT >= 0 && this._dropT < DROP_FOCUS_BEATS ? PAT_DROP_FOCUS : this._pat;
    if (live === PAT_DROP_FOCUS || this._patFade >= 0.999) return this._stepNormal(m, live, stepMix, out);
    const a = this._stepNormal(m, this._patPrev, stepMix, this._v[3]);
    this._stepNormal(m, live, stepMix, out);
    // out already holds the live pattern's normal: lerp TOWARDS a by the
    // remaining weight, never copy(a) first - that would overwrite it.
    out.lerp(a, 1 - this._patFade);
    const l = out.length();
    return l > 1e-5 ? out.multiplyScalar(1 / l) : this._stepNormal(m, live, stepMix, out);
  }

  _advanceStep() {
    this._step++;
    this._sinceStep = 0;
    this._bmPrev = this._bmCur;
    this._bmCur = this._spread * (1 + this._burst * 0.6);
    if (this._unfoldDueAtStep >= 0 && this._step >= this._unfoldDueAtStep) {
      this._beginUnfold();
      this._unfoldDueAtStep = this._debugUnfold ? this._step + 8 : -1;
    }
  }

  // The phrase: a new motif, a new colour, the dichroic roll, and the
  // unfolding - from this instant the source is on ONE mirror only.
  _onPhrase() {
    this._colorIndex++;
    this._dichroic = this._layout.dichroic && (this._debugDichroic || Math.random() < DICHROIC_CHANCE);
    this._setColor();
    this._changePattern();
    this._beginUnfold();
  }

  _beginUnfold() {
    this._unfoldT = 0;
  }

  // Phrases alternate: a dominant one (PARALLEL and FOCUS in turn), then one
  // of the secondary patterns, so the sheet of beams moving together is on
  // screen at least one phrase in two.
  _changePattern() {
    if (this._forcedPattern != null) return;
    this._patPrev = this._pat;
    this._phraseCount++;
    if (this._phraseCount % 2 === 0) {
      this._pat = this._dominantNext;
      this._dominantNext = this._dominantNext === PAT_PARALLEL ? PAT_FOCUS : PAT_PARALLEL;
    } else {
      this._patCursor++;
      if (this._patCursor >= this._patOrder.length) {
        this._patOrder = this._shuffledPatterns();
        this._patCursor = 0;
      }
      this._pat = this._patOrder[this._patCursor];
    }
    this._patFade = 0;
    if (this._pat === PAT_FOCUS) this._pickFocal();
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };
    const bar = profile.barPhase;
    const beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  // The drop: FOCUS on the viewer with the guard off and the gain up, then
  // PARALLEL boosted, raking the whole room. In beats, so it keeps its shape
  // at any tempo.
  _stepDrop(deltaTime) {
    if (this._dropT < 0) { this._burst = 0; return; }
    this._dropT += deltaTime / this._beatPeriod;
    if (this._dropT < DROP_FOCUS_BEATS) {
      const t = this._dropT / DROP_FOCUS_BEATS;
      this._burst = 0;
      this._dazzle = Math.sin(t * Math.PI);
    } else if (this._dropT < DROP_TOTAL_BEATS) {
      const t = (this._dropT - DROP_FOCUS_BEATS) / DROP_SWEEP_BEATS;
      this._burst = 1 - t * t;
      this._dazzle = 0;
    } else {
      this._dropT = -1;
      this._burst = 0;
      this._dazzle = 0;
    }
  }

  // The frame's whole geometry: normals, reflections, beams, impacts.
  _stepChoreography(stepMix) {
    const mirrors = this._mirrors;
    const N = this._count;
    const unfolding = this._unfoldT < UNFOLD_BEATS;
    const lit = this._v[0];
    const hit = this._v[4];
    const origin = this._emitOrigin;
    const impacts = !!this._impacts;
    const focusing = this._pat === PAT_FOCUS || (this._dropT >= 0 && this._dropT < DROP_FOCUS_BEATS);
    const reflGain = focusing ? FOCUS_GAIN : 1;
    // Density: 64 full beams saturate, so the WEAK beats (2 and 4 of the
    // bar) light every other row only and the strong beats light them all -
    // 32 then 64, the contrast itself an event. Which half rests alternates
    // every bar. The unfolding, the drop and the single-mirror probe own
    // their own activation.
    const weak = this._step % 2 === 1 && !unfolding && this._dropT < 0 && !this._single;
    const restParity = (this._step >> 2) & 1;
    let litCount = 0;

    for (let k = 0; k < N; k++) {
      const m = mirrors[k];
      // Orientation first - the gimbal moves whether or not the beam is on it.
      this._mirrorNormal(m, stepMix, m.normal);
      // r = d - 2 (d . n) n, exactly.
      const d = m.incident;
      const n = m.normal;
      const dn = d.x * n.x + d.y * n.y + d.z * n.z;
      m.reflected.set(d.x - 2 * dn * n.x, d.y - 2 * dn * n.y, d.z - 2 * dn * n.z);

      // Activation: the unfolding gates mirror k by its sweep order; the
      // single-mirror probe keeps only the first.
      let active = 1;
      if (this._single) active = k === 0 ? 1 : 0;
      else if (unfolding) active = this._unfoldT >= m.unfoldAt ? 1 : 0;
      else if (weak && ((m.row + restParity) & 1)) active = 0;
      m.active = active;
      litCount += active;

      // Colours: the incident beam is the source's; the reflection is the
      // row's filter when the dichroic phrase is on.
      const rc = this._dichroic ? this._rowColors[m.row] : this._baseColor;

      // Incident: from the emitter to the mirror's centre, cut just short of
      // the glass so the dot on the plate marks the arrival.
      lit.copy(m.pos).sub(origin);
      const incLen = lit.length();
      lit.multiplyScalar(1 / incLen);
      this._bOrigin.setXYZ(k, origin.x, origin.y, origin.z);
      this._bDir.setXYZ(k, lit.x, lit.y, lit.z);
      this._bLen.setX(k, incLen - 0.03);
      this._bGain.setX(k, active);
      this._bColor.setXYZ(k, this._baseColor.r, this._baseColor.g, this._baseColor.b);

      // Reflected: from the glass, until the floor, the backdrop or the haze.
      const r = m.reflected;
      let len = BEAM_REACH;
      let floorT = -1;
      if (r.y < -0.02) {
        floorT = (m.pos.y - 0.02) / -r.y;
        if (floorT < len) len = floorT;
      }
      if (r.z < -0.02) {
        const backT = (m.pos.z - (GRID_Z - 1.3)) / -r.z;
        if (backT < len) len = backT;
      }
      const j = N + k;
      const ox = m.pos.x + n.x * (PLATE_DEPTH / 2 + 0.01);
      const oy = m.pos.y + n.y * (PLATE_DEPTH / 2 + 0.01);
      const oz = m.pos.z + n.z * (PLATE_DEPTH / 2 + 0.01);
      this._bOrigin.setXYZ(j, ox, oy, oz);
      this._bDir.setXYZ(j, r.x, r.y, r.z);
      this._bLen.setX(j, len);
      this._bGain.setX(j, active * reflGain);
      this._bColor.setXYZ(j, rc.r, rc.g, rc.b);

      // The mirror's own light.
      this._mNormal.setXYZ(k, n.x, n.y, n.z);
      this._mLit.setX(k, active);

      if (impacts) {
        const within = floorT > 0 && floorT <= BEAM_REACH ? 1 : 0;
        if (within) {
          hit.set(m.pos.x + r.x * floorT, 0, m.pos.z + r.z * floorT);
          const fl = Math.sqrt(r.x * r.x + r.z * r.z);
          const fx = fl > 1e-4 ? r.x / fl : 0, fz = fl > 1e-4 ? r.z / fl : 1;
          const stretch = 1 / Math.max(-r.y, 0.18);
          // A long throw lands faint - the beam's own extinction.
          const reach = Math.exp(-floorT * BEAM_EXT) * (1 - clamp01((floorT - BEAM_FADE_FROM) / (BEAM_REACH - BEAM_FADE_FROM)));
          this._iHit.setXYZ(k, hit.x, hit.y, hit.z);
          this._iFwd.setXYZ(k, fx, 0, fz);
          this._iStretch.setX(k, stretch);
          this._iGain.setX(k, active * reach);
          this._iColor.setXYZ(k, rc.r, rc.g, rc.b);
        } else {
          this._iGain.setX(k, 0);
        }
      }
    }
    this._litCount = litCount;

    if (impacts) {
      // The floor spot at the source: just ahead of the aperture, along the
      // housing's yaw, stretched the way the beam leaves.
      const a = this._aimFlat;
      const e = this._emitOrigin;
      this._iHit.setXYZ(N, e.x + a.x * FLOOR_SPOT_AHEAD, 0, e.z + a.z * FLOOR_SPOT_AHEAD);
      this._iFwd.setXYZ(N, a.x, 0, a.z);
      this._iStretch.setX(N, FLOOR_SPOT_STRETCH);
      this._iGain.setX(N, FLOOR_SPOT_GAIN);
      this._iColor.setXYZ(N, this._baseColor.r, this._baseColor.g, this._baseColor.b);
    }

    this._bOrigin.needsUpdate = true;
    this._bDir.needsUpdate = true;
    this._bLen.needsUpdate = true;
    this._bGain.needsUpdate = true;
    this._bColor.needsUpdate = true;
    this._mNormal.needsUpdate = true;
    this._mLit.needsUpdate = true;
    if (impacts) {
      this._iHit.needsUpdate = true;
      this._iFwd.needsUpdate = true;
      this._iStretch.needsUpdate = true;
      this._iGain.needsUpdate = true;
      this._iColor.needsUpdate = true;
    }
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return; // gel

    this._time += deltaTime;
    this._sinceStep += deltaTime;
    const director = profile.director;
    const u = this._uniforms;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;

    this._stepSynthBands(deltaTime, profile);
    const bands = this._effectiveBands(director);

    // --- the notch: one per beat, falling back to the tempo clock ---
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) this._advanceStep();
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) {
      this._advanceStep();
    }
    const e = clamp01(this._sinceStep * 1000 / MOVE_MS);
    const stepMix = e * e * (3 - 2 * e);

    // --- the phrase: half a beat of black, then the unfolding ---
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    let blackout = false;
    if (phrasePhase != null) {
      blackout = phrasePhase > 1 - BLACKOUT_PHRASE_FRAC;
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase();
      this._lastPhrasePhase = phrasePhase;
    } else if (!this._debugUnfold) {
      // No phrase grid: a phrase every 16 notches, the blackout on the last
      // half of the 16th.
      const inPhrase = this._step % BEATS_PER_PHRASE;
      blackout = inPhrase === BEATS_PER_PHRASE - 1 && this._sinceStep > this._beatPeriod * 0.5;
      if (inPhrase === 0 && this._sinceStep === 0 && this._step > 0) this._onPhrase();
    }
    if (this._patFade < 1) {
      this._patFade = clamp01(this._patFade + deltaTime / (this._beatPeriod * PATTERN_FADE_BEATS));
    }
    if (this._unfoldT < UNFOLD_BEATS) this._unfoldT += deltaTime / this._beatPeriod;

    // --- the drop ---
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop' && this._dropT < 0) {
      this._dropT = 0;
      this._dazzle = 0;
      this._unfoldT = UNFOLD_BEATS;     // a drop cuts any unfolding short
      this._pickDropFocal();
      if (this._forcedPattern == null) {
        this._patPrev = PAT_PARALLEL;
        this._pat = PAT_PARALLEL;
        this._patFade = 1;
      }
    }
    this._lastMode = mode;
    this._stepDrop(deltaTime);

    // --- bands: low opens the patterns; the dazzle alone widens the halo ---
    const low = bands && bands.low != null ? bands.low : 0.5;
    this._spread += (0.80 + 0.40 * low - this._spread) * Math.min(1, deltaTime / 0.25);
    u.uOuterPx.value = REFLECTED_PX + this._dazzle * 4.0;

    this._stepChoreography(stepMix);

    // --- the source: off for 30 ms on every beat, off for the blackout ---
    const pulse = this._sinceStep < PULSE_OFF_S ? 0 : 1;
    const dazzleGain = 1 + this._dazzle * (DROP_DAZZLE_GAIN - 1);
    u.uPulse.value = pulse;
    u.uMasterGain.value = blackout ? 0 : dazzleGain * (1 + this._burst * 0.35);
    u.uGuardOff.value = this._dazzle > 0 ? 1 : 0;

    // Pixels per world unit at unit view depth: the beams' width floor.
    const tanHalf = Math.tan((this._camera.fov * DEG) / 2);
    const bufferHeight = getRenderBufferHeight() || 1080;
    u.uPixelScale.value = bufferHeight / (2 * tanHalf);
    // The flare's streak: a fraction of the frame's WIDTH, in pixels.
    const aspect = this._camera.aspect || (16 / 9);
    u.uFlareHalf.value.set(FLARE_STREAK_FRAC * 0.5 * bufferHeight * aspect, FLARE_HALF_H_PX);

    if (this._probe && typeof window !== 'undefined') window.__mirrorarray = this.getProbe();

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
  }

  // The live state, for the acceptance checks: every mirror's incident
  // direction, normal and reflection, the source, the step counter, the pulse.
  getProbe() {
    const origin = this._emitOrigin;
    const list = [];
    for (let k = 0; k < this._count; k++) {
      const m = this._mirrors[k];
      list.push({
        col: m.col, row: m.row, order: k,
        pos: [m.pos.x, m.pos.y, m.pos.z],
        normal: [m.normal.x, m.normal.y, m.normal.z],
        incident: [m.incident.x, m.incident.y, m.incident.z],
        reflected: [m.reflected.x, m.reflected.y, m.reflected.z],
        active: m.active
      });
    }
    return {
      t: this._time,
      step: this._step,
      sinceStep: this._sinceStep,
      pulse: this._uniforms.uPulse.value,
      master: this._uniforms.uMasterGain.value,
      pattern: this._pat,
      patternPrev: this._patPrev,
      patternMix: this._patFade,
      unfoldT: this._unfoldT,
      litCount: this._litCount,
      dichroic: this._dichroic,
      drop: this._dropT,
      focal: [this._focal.x, this._focal.y, this._focal.z],
      dropFocal: [this._dropFocal.x, this._dropFocal.y, this._dropFocal.z],
      source: [origin.x, origin.y, origin.z],
      mirrors: list
    };
  }

  dispose() {
    this._inner.fog = null;
    if (this._quad) {
      this._quad.onBeforeRender = null;
      this._scene.remove(this._quad);
      this._quad.geometry.dispose();
      this._quadMat.dispose();
      this._rt.dispose();
      this._quad = null;
    }
    this._inner.remove(this._floor);
    this._floorGeometry.dispose();
    this._floorMaterial.dispose();
    this._inner.remove(this._backdrop);
    this._backGeometry.dispose();
    this._backMaterial.dispose();
    this._inner.remove(this._beams);
    this._beamGeometry.dispose();       // the MATERIALS are cached - see sharedMaterials
    if (this._impacts) {
      this._inner.remove(this._impacts);
      if (this._reflection) this._inner.remove(this._reflection);
      this._impactGeometry.dispose();
      this._impacts = null;
      this._reflection = null;
    }
    this._inner.remove(this._plates);
    this._plateGeometry.dispose();
    this._inner.remove(this._forks);
    this._forkGeometry.dispose();
    this._inner.remove(this._chassis);
    this._chassisGeometry.dispose();
    this._inner.remove(this._housing);
    this._housingGeometry.dispose();
    this._shadowGeometry.dispose();
    this._inner.remove(this._flare);
    this._flareGeometry.dispose();
    if (typeof window !== 'undefined' && window.__mirrorarray) delete window.__mirrorarray;
  }
}
